// src/ui/CreatePostDialog.jsx — "Create post", the browser's twin of the phone's
// full-screen composer (app/src/components/PostComposer.tsx).
//
//   CREATE POST   who is posting · how they feel · who is with them · where
//                 the words (the same TextPostCard the feed draws), photos,
//                 the poll — and "Add to your post": Photo/video · Tag people ·
//                 Feeling/activity · Check in · Poll · Background · Music
//   NEXT          a preview, then Post as · Post audience · Add music ·
//                 Comments · AI label — and the Post button
//   FEELINGS      "How are you feeling?" — Feelings | Activities, search, grid
//   MUSIC         search, the shelves, ▶ to preview
//   ✕ / Esc       "Save this post as a draft?" rising from the bottom:
//                 Save as draft · Discard post · Continue editing
//
// 🟢 The state stays in VibesScreen, where submitPost already read it; this only
//    lays it out. Words come from services/postCompose (shared with the phone).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Pic from './VibeAvatar';
import {
  X, Back, Image as ImageIcon, MapPin, Plane, Poll, Tag, Smile, Globe, Group as GroupIcon, ChevronRight,
  Search as SearchIcon, Play, Pause, Check, Sparkles, Comment, Bookmark, Trash, Type,
} from './icons';
import {
  composeL, searchFeelings, searchActivities, feelingLabel, activityLabel, activityAsk,
  musicFromSound, ACTIVITY_TEXT_MAX, feelingPieces, feelingChipPieces, placeAfterFeelingPieces, iconizePlace, activityIcon,
} from '../services/postCompose';
import { jamendoTracksUrl, usableSounds, SOUND_CATEGORIES, fmtDuration } from '../services/soundLibrary';
import LinePieces, { LineGlyph } from './LinePieces';   // 🧭 minimal icons for activities, the pin, the plane

const langNow = () => (typeof navigator !== 'undefined' ? (navigator.language || 'en') : 'en').slice(0, 2);

function MusicNote(props) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="currentColor" aria-hidden {...props}>
      <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
    </svg>
  );
}

export default function CreatePostDialog(p) {
  const lang = langNow();
  const L = (k, v) => composeL(k, lang, v);
  const [page, setPage] = useState('edit');      // 'edit' | 'settings'
  const [sub, setSub] = useState(null);          // null | 'feeling' | 'music' | 'audience' | 'postas'
  const [leave, setLeave] = useState(false);

  const requestLeave = () => {
    if (p.posting) return;
    if (p.hasContent) setLeave(true); else p.onLeave('empty');
  };
  const back = () => {
    if (leave) { setLeave(false); return; }
    if (sub) { setSub(null); return; }
    if (page === 'settings') { setPage('edit'); return; }
    requestLeave();
  };
  /* Esc goes back one step, like the ← */
  const backRef = useRef(back);
  backRef.current = back;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (p.blockEsc) return;   // a picker above us owns the key
      e.preventDefault();
      backRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [p.blockEsc]);

  const headline = () => (
    <span className="cp-headline">
      <b>{p.author.name}</b>
      {p.feeling ? <span className="cp-link" onClick={() => setSub('feeling')}><LinePieces pieces={feelingPieces(p.feeling, lang)} /></span> : null}
      {p.status ? (
        <span className="cp-link" onClick={p.onOpenStatus}>
          <LinePieces pieces={p.feeling ? placeAfterFeelingPieces(p.status.type, lang) : iconizePlace(p.status.type === 'check-in' ? ' · 📍 ' : ' · ✈️ ')} />
          <b>{p.status.type === 'check-in' ? p.status.location : p.status.to}</b>
        </span>
      ) : null}
      {p.tags.length ? (
        <span className="cp-link" onClick={p.onOpenTags}>
          {` — ${L('with')} `}<b>{p.tags[0].name}</b>{p.tags.length > 1 ? ` +${p.tags.length - 1}` : ''}
        </span>
      ) : null}
    </span>
  );

  const audienceChip = (
    <button type="button" className="cp-chip" onClick={() => setSub('audience')}>
      {p.audience === 'friends' ? <GroupIcon width={13} height={13} /> : <Globe width={13} height={13} />}
      <span>{L(p.audience === 'friends' ? 'friends' : 'public')}</span>
      <span className="cp-caret">▾</span>
    </button>
  );

  const tools = [
    { key: 'media', I: ImageIcon, color: '#45BD62', label: L('photo_video'), on: p.mediaCount > 0, go: p.onPickMedia },
    { key: 'tag', I: Tag, color: '#1877F2', label: L('tag_people'), on: p.tags.length > 0, go: p.onOpenTags },
    { key: 'feeling', I: Smile, color: '#F7B928', label: L('feeling_activity'), on: !!p.feeling, go: () => setSub('feeling') },
    { key: 'place', I: MapPin, color: '#F5533D', label: L('check_in'), on: !!p.status, go: p.onOpenStatus },
    { key: 'poll', I: Poll, color: '#F0932B', label: L('poll'), on: p.pollOn, go: p.onPoll },
    { key: 'bg', I: Type, color: '#2ABBA7', label: L('background'), on: p.styleOn, go: p.onBackground },
    ...(p.hasVideo ? [] : [{ key: 'music', I: MusicNote, color: '#E4405F', label: L('add_music'), on: !!p.music, go: () => setSub('music') }]),
  ];
  const nextOk = p.canPost && !p.posting && !p.banned;

  /* ── feelings ─────────────────────────────────────────────────────── */
  const [fTab, setFTab] = useState('feelings');
  const [fq, setFq] = useState('');
  const [act, setAct] = useState(null);
  const [actText, setActText] = useState('');
  useEffect(() => {
    if (sub !== 'feeling') return;
    setFq(''); setAct(null);
    setActText(p.feeling && p.feeling.kind === 'activity' ? p.feeling.text || '' : '');
    setFTab(p.feeling && p.feeling.kind === 'activity' ? 'activities' : 'feelings');
  }, [sub]); // eslint-disable-line react-hooks/exhaustive-deps
  const feelRows = useMemo(() => (fTab === 'feelings' ? searchFeelings(fq, lang) : searchActivities(fq, lang)), [fTab, fq, lang]);
  const commitActivity = () => {
    if (!act || !actText.trim()) return;
    p.onFeeling({ kind: 'activity', key: act, text: actText.trim() });
    setSub(null);
  };

  /* ── music ────────────────────────────────────────────────────────── */
  const [shelf, setShelf] = useState('trending');
  const [mq, setMq] = useState('');
  const [tracks, setTracks] = useState([]);
  const [mBusy, setMBusy] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const audioRef = useRef(null);
  const stopPreview = () => {
    try { if (audioRef.current) audioRef.current.pause(); } catch { /* gone */ }
    audioRef.current = null;
    setPreviewId(null);
  };
  useEffect(() => {
    if (sub !== 'music') { stopPreview(); return undefined; }
    let live = true;
    const tm = setTimeout(async () => {
      setMBusy(true);
      try {
        const k = SOUND_CATEGORIES.find((x) => x.key === shelf);
        const r = await fetch(jamendoTracksUrl({ search: mq.trim() || null, tag: mq.trim() ? null : (k ? k.tag : null), limit: 40 }));
        const j = await r.json();
        if (live) setTracks(usableSounds(j && j.results));
      } catch { if (live) setTracks([]); }
      if (live) setMBusy(false);
    }, mq ? 350 : 0);
    return () => { live = false; clearTimeout(tm); };
  }, [sub, shelf, mq]);
  useEffect(() => () => stopPreview(), []);
  const preview = (t) => {
    if (previewId === t.id) { stopPreview(); return; }
    stopPreview();
    try {
      const a = new Audio(t.url);
      audioRef.current = a;
      setPreviewId(t.id);
      a.play().catch(() => setPreviewId(null));
      a.onended = () => setPreviewId(null);
    } catch { setPreviewId(null); }
  };

  const subHead = (title, onBack, right) => (
    <div className="cp-head">
      <button type="button" className="cp-round" onClick={onBack} aria-label="Back"><Back width={18} height={18} /></button>
      <b>{title}</b>
      {right || <span style={{ width: 36 }} />}
    </div>
  );
  const searchBox = (value, set, ph, busy) => (
    <div className="cp-search">
      <SearchIcon width={16} height={16} />
      <input value={value} onChange={(e) => set(e.target.value)} placeholder={ph} autoFocus />
      {busy ? <span className="cp-spin" /> : null}
    </div>
  );

  let body;
  if (sub === 'feeling' && act) {
    body = (
      <>
        {subHead(activityLabel(act, lang), () => setAct(null), (
          <button type="button" className="cp-next" disabled={!actText.trim()} onClick={commitActivity}>{L('done')}</button>
        ))}
        <div className="cp-pad">
          <input
            className="cp-actinput" autoFocus value={actText} maxLength={ACTIVITY_TEXT_MAX}
            placeholder={activityAsk(act, lang)}
            onChange={(e) => setActText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitActivity(); }}
          />
          <div className="cp-sub"><LinePieces pieces={feelingPieces({ kind: 'activity', key: act, text: actText.trim() || '…' }, lang)} /></div>
        </div>
      </>
    );
  } else if (sub === 'feeling') {
    body = (
      <>
        {subHead(L('how_feeling'), () => setSub(null))}
        <div className="cp-tabs" role="tablist">
          {['feelings', 'activities'].map((k) => (
            <button key={k} type="button" role="tab" aria-selected={fTab === k} className={fTab === k ? 'on' : ''} onClick={() => setFTab(k)}>{L(k)}</button>
          ))}
        </div>
        <div className="cp-pad">{searchBox(fq, setFq, L('search'))}</div>
        {p.feeling ? (
          <button type="button" className="cp-current" onClick={() => { p.onFeeling(null); setSub(null); }}>
            <span><LinePieces pieces={feelingChipPieces(p.feeling, lang)} /></span><b>{L('remove')}</b>
          </button>
        ) : null}
        <div className="cp-grid">
          {feelRows.map((x) => (
            <button
              key={x.key} type="button"
              className={`cp-cell ${p.feeling && p.feeling.key === x.key ? 'on' : ''}`}
              onClick={() => {
                if (fTab === 'feelings') { p.onFeeling({ kind: 'feeling', key: x.key }); setSub(null); } else { setActText(p.feeling && p.feeling.key === x.key ? (p.feeling.text || '') : ''); setAct(x.key); }
              }}
            >
              {fTab === 'activities' && activityIcon(x.key)
                ? <span className="cp-emoji cp-acticon"><LineGlyph icon={activityIcon(x.key)} size={18} /></span>
                : <span className="cp-emoji">{x.emoji}</span>}
              <span>{fTab === 'feelings' ? feelingLabel(x.key, lang) : activityLabel(x.key, lang)}</span>
            </button>
          ))}
          {!feelRows.length && <div className="cp-sub" style={{ padding: 16 }}>{L('no_results')}</div>}
        </div>
      </>
    );
  } else if (sub === 'music') {
    body = (
      <>
        {subHead(L('music'), () => setSub(null))}
        <div className="cp-pad">{searchBox(mq, setMq, L('search_music'), mBusy)}</div>
        {!mq && (
          <div className="cp-shelves">
            {SOUND_CATEGORIES.map((k) => (
              <button key={k.key} type="button" className={shelf === k.key ? 'on' : ''} onClick={() => setShelf(k.key)}>{k.label}</button>
            ))}
          </div>
        )}
        <div className="cp-tracks">
          {tracks.map((t) => (
            <div key={t.id} className={`cp-track ${p.music && p.music.id === t.id ? 'on' : ''}`}>
              <button type="button" className="cp-cover" onClick={() => preview(t)} aria-label="Preview" style={t.cover ? { backgroundImage: `url(${t.cover})` } : undefined}>
                {previewId === t.id ? <Pause width={16} height={16} /> : <Play width={16} height={16} />}
              </button>
              <button type="button" className="cp-trackname" onClick={() => { stopPreview(); p.onMusic(musicFromSound(t)); setSub(null); }}>
                <b>{t.title}</b>
                <span>{t.artist} · {fmtDuration(t.duration)}</span>
              </button>
              {p.music && p.music.id === t.id ? <Check width={18} height={18} /> : null}
            </div>
          ))}
          {!mBusy && !tracks.length && <div className="cp-sub" style={{ padding: 16 }}>{L('no_results')}</div>}
        </div>
        <div className="cp-sub cp-credit">Music via Jamendo</div>
      </>
    );
  } else if (sub === 'audience') {
    body = (
      <>
        {subHead(L('post_audience'), () => setSub(null))}
        <div className="cp-pad"><b className="cp-q">{L('who_can_see')}</b></div>
        {[['everyone', Globe, 'public', 'public_hint'], ['friends', GroupIcon, 'friends', 'friends_hint']].map(([val, I, label, hint]) => (
          <button key={val} type="button" className="cp-opt" onClick={() => { p.onAudience(val); setSub(null); }} aria-pressed={p.audience === val}>
            <span className="cp-optic"><I width={20} height={20} /></span>
            <span className="cp-opttext"><b>{L(label)}</b><span>{L(hint)}</span></span>
            <span className={`cp-radio ${p.audience === val ? 'on' : ''}`} />
          </button>
        ))}
      </>
    );
  } else if (sub === 'postas') {
    body = (
      <>
        {subHead(L('post_as'), () => setSub(null))}
        {[null, ...p.pages].map((pg) => {
          const on = pg ? p.postAsId === pg.id : p.postAsId === 'me';
          return (
            <button key={pg ? pg.id : '__me'} type="button" className="cp-opt" onClick={() => { p.onPostAs(pg ? pg.id : 'me'); setSub(null); }} aria-pressed={on}>
              <Pic src={pg ? pg.profileImage : p.me.profileImage} name={pg ? pg.name : p.me.fullName} size={40} />
              <span className="cp-opttext"><b>{pg ? pg.name : (p.me.fullName || L('you'))}</b></span>
              <span className={`cp-radio ${on ? 'on' : ''}`} />
            </button>
          );
        })}
      </>
    );
  } else if (page === 'settings') {
    const text = String(p.text || '').trim();
    const big = !p.mediaCount && text && text.length <= 160;
    body = (
      <>
        {subHead(L('new_post'), () => setPage('edit'))}
        <div className="cp-scroll">
          <div className="cp-preview" onClick={() => setPage('edit')} role="button" tabIndex={0}>
            <div className="cp-author">
              <Pic src={p.author.image} name={p.author.name} size={40} />
              <div>
                <div className="cp-prevname" onClick={(e) => { if (p.pages.length) { e.stopPropagation(); setSub('postas'); } }}>
                  {headline()}{p.pages.length ? <ChevronRight width={14} height={14} /> : null}
                </div>
                <div className="cp-sub">{p.audience === 'friends' ? <GroupIcon width={11} height={11} /> : <Globe width={11} height={11} />} {L(p.audience === 'friends' ? 'friends' : 'public')}</div>
              </div>
            </div>
            {text ? <div className={`cp-prevtext ${big ? 'big' : ''}`}>{text}</div> : null}
            {p.mediaCount ? <div className="cp-sub">🖼 {L(p.mediaCount === 1 ? 'draft_one_item' : 'draft_items', { n: p.mediaCount })}</div> : null}
            {p.pollOn ? <div className="cp-sub">📊 {L('poll')}</div> : null}
          </div>

          {p.pages.length > 0 && (
            <button type="button" className="cp-row" onClick={() => setSub('postas')}>
              <span className="cp-optic"><GroupIcon width={18} height={18} /></span>
              <span className="cp-opttext"><b>{L('post_as')}</b><span>{p.postAsId === 'me' ? (p.me.fullName || L('you')) : ((p.pages.find((x) => x.id === p.postAsId) || {}).name || '')}</span></span>
              <ChevronRight width={16} height={16} />
            </button>
          )}
          <button type="button" className="cp-row" onClick={() => setSub('audience')}>
            <span className="cp-optic">{p.audience === 'friends' ? <GroupIcon width={18} height={18} /> : <Globe width={18} height={18} />}</span>
            <span className="cp-opttext"><b>{L('post_audience')}</b><span>{L(p.audience === 'friends' ? 'friends' : 'public')}</span></span>
            <ChevronRight width={16} height={16} />
          </button>
          <div className="cp-row static">
            <span className="cp-optic"><MusicNote /></span>
            <span className="cp-opttext">
              <b>{L('add_music')}</b>
              {p.hasVideo ? <span>{L('music_video_note')}</span> : p.music ? (
                <span className="cp-picked">
                  ♪ {p.music.title}{p.music.artist ? ` · ${p.music.artist}` : ''}
                  <button type="button" onClick={() => p.onMusic(null)} aria-label={L('remove')}><X width={12} height={12} /></button>
                </span>
              ) : (
                <span className="cp-shelves inline">
                  {SOUND_CATEGORIES.slice(0, 5).map((k) => (
                    <button key={k.key} type="button" onClick={() => { setShelf(k.key); setSub('music'); }}>♪ {k.label}</button>
                  ))}
                </span>
              )}
            </span>
            {!p.hasVideo && !p.music ? <button type="button" className="cp-round" onClick={() => setSub('music')} aria-label={L('add_music')}><ChevronRight width={16} height={16} /></button> : null}
          </div>
          <label className="cp-row">
            <span className="cp-optic"><Comment width={18} height={18} /></span>
            <span className="cp-opttext"><b>{L('comments')}</b><span>{L(p.comments ? 'comments_on' : 'comments_off')}</span></span>
            <input type="checkbox" className="cp-switch" checked={p.comments} onChange={(e) => p.onComments(e.target.checked)} />
          </label>
          <label className="cp-row">
            <span className="cp-optic"><Sparkles width={18} height={18} /></span>
            <span className="cp-opttext"><b>{L('ai_label')}</b><span>{L('ai_label_hint')}</span></span>
            <input type="checkbox" className="cp-switch" checked={p.ai} onChange={() => p.onAi(!p.ai)} aria-label={L('ai_label')} />
          </label>
        </div>
        <div className="cp-foot">
          <button type="button" className="cp-post" disabled={!nextOk} onClick={p.onPost}>{p.posting ? '…' : L('post')}</button>
        </div>
      </>
    );
  } else {
    body = (
      <>
        <div className="cp-head">
          <span style={{ width: 36 }} />
          <b>{L('create_post')}</b>
          <button type="button" className="cp-round" onClick={requestLeave} aria-label={L('discard')}><X width={18} height={18} /></button>
        </div>
        <div className="cp-scroll">
          <div className="cp-author">
            <Pic src={p.author.image} name={p.author.name} size={44} />
            <div>
              <div>{headline()}</div>
              <div className="cp-chips">
                {audienceChip}
                {p.feeling ? <button type="button" className="cp-chip" onClick={() => p.onFeeling(null)} title={L('remove')}><LinePieces pieces={feelingChipPieces(p.feeling, lang)} size={12} /> <X width={11} height={11} /></button> : null}
                {p.status ? (
                  <button type="button" className="cp-chip" onClick={p.onClearStatus} title={L('remove')}>
                    <LineGlyph icon={p.status.type === 'check-in' ? 'pin' : 'plane'} size={12} />
                    {p.status.type === 'check-in' ? p.status.location : p.status.to} <X width={11} height={11} />
                  </button>
                ) : null}
                {p.music ? <button type="button" className="cp-chip" onClick={() => setSub('music')}>♪ {p.music.title}</button> : null}
              </div>
            </div>
          </div>
          <div className="cp-editor">{p.editor}</div>
          {p.extras}
        </div>
        <div className="cp-addbar">
          <span>{L('add_to_post')}</span>
          <div>
            {tools.map(({ key, I, color, label, on, go }) => (
              <button key={key} type="button" className={`cp-tool ${on ? 'on' : ''}`} title={label} aria-label={label} aria-pressed={!!on} onClick={go} style={{ color }}>
                <I width={20} height={20} />
              </button>
            ))}
          </div>
        </div>
        <div className="cp-foot">
          <button type="button" className="cp-post" disabled={!nextOk} onClick={() => setPage('settings')}>{L('next')}</button>
        </div>
      </>
    );
  }

  return (
    <div className="cp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) requestLeave(); }}>
      <div className="cp-card" role="dialog" aria-modal="true" aria-label={L('create_post')}>
        {body}
        {p.notice ? <div className="cp-notice">{p.notice}</div> : null}
        {leave && (
          <div className="cp-leave-dim" onMouseDown={(e) => { if (e.target === e.currentTarget) setLeave(false); }}>
            <div className="cp-leave" role="alertdialog" aria-label={L('leave_q')}>
              <div className="cp-grab" />
              <b>{L('leave_q')}</b>
              <p>{L('discard_hint')}</p>
              <button type="button" onClick={() => { setLeave(false); p.onLeave('save'); }}>
                <span className="cp-optic"><Bookmark width={18} height={18} /></span>
                <span className="cp-opttext"><b>{L('save_draft')}</b><span>{L('save_draft_hint')}</span></span>
              </button>
              <button type="button" className="danger" onClick={() => { setLeave(false); p.onLeave('discard'); }}>
                <span className="cp-optic"><Trash width={18} height={18} /></span>
                <span className="cp-opttext"><b>{L('discard')}</b></span>
              </button>
              <button type="button" onClick={() => setLeave(false)}>
                <span className="cp-optic"><Check width={18} height={18} /></span>
                <span className="cp-opttext"><b>{L('keep_editing')}</b></span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
